import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authClient } from "../auth/auth-client";

type Mode = "sign-in" | "sign-up";

type FormState = {
  name: string;
  email: string;
  password: string;
};

const defaultForm: FormState = {
  name: "",
  email: "",
  password: "",
};

const RESEND_COOLDOWN_SECONDS = 60;
const RESEND_MAX_ATTEMPTS = 3;
const RESEND_WINDOW_MS = 15 * 60 * 1000;
const RESEND_STORAGE_KEY = "rr_verify_resend_meta";

type ResendMeta = {
  lastSentAt: number;
  windowStartAt: number;
  attemptCount: number;
};

function readResendMeta(): ResendMeta | null {
  try {
    const raw = localStorage.getItem(RESEND_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ResendMeta;
    if (
      typeof parsed.lastSentAt !== "number" ||
      typeof parsed.windowStartAt !== "number" ||
      typeof parsed.attemptCount !== "number"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeResendMeta(meta: ResendMeta) {
  localStorage.setItem(RESEND_STORAGE_KEY, JSON.stringify(meta));
}

function getResendState(now: number) {
  const meta = readResendMeta();
  if (!meta) {
    return {
      canSend: true,
      cooldownRemaining: 0,
      attemptsRemaining: RESEND_MAX_ATTEMPTS,
    };
  }

  const inWindow = now - meta.windowStartAt < RESEND_WINDOW_MS;
  const windowAttemptCount = inWindow ? meta.attemptCount : 0;
  const attemptsRemaining = Math.max(0, RESEND_MAX_ATTEMPTS - windowAttemptCount);
  const cooldownRemaining = Math.max(
    0,
    RESEND_COOLDOWN_SECONDS - Math.floor((now - meta.lastSentAt) / 1000),
  );

  return {
    canSend: attemptsRemaining > 0 && cooldownRemaining <= 0,
    cooldownRemaining,
    attemptsRemaining,
  };
}

function registerResendAttempt(now: number) {
  const previous = readResendMeta();
  const resetWindow = !previous || now - previous.windowStartAt >= RESEND_WINDOW_MS;
  const nextMeta: ResendMeta = {
    lastSentAt: now,
    windowStartAt: resetWindow ? now : previous.windowStartAt,
    attemptCount: resetWindow ? 1 : previous.attemptCount + 1,
  };
  writeResendMeta(nextMeta);
}

export default function Login() {
  const navigate = useNavigate();
  const appURL = (process.env.REACT_APP_FRONTEND_URL || window.location.origin).replace(
    /\/$/,
    "",
  );
  const loginURL = `${appURL}/login`;
  const query = new URLSearchParams(window.location.search);
  const modeParam = query.get("mode");
  const nextParam = query.get("next");
  const postAuthPath =
    nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")
      ? nextParam
      : "/";
  const [mode, setMode] = useState<Mode>(modeParam === "sign-up" ? "sign-up" : "sign-in");
  const [form, setForm] = useState<FormState>(defaultForm);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState(RESEND_MAX_ATTEMPTS);

  const isSignUp = mode === "sign-up";

  const heading = useMemo(() => {
    if (mode === "sign-up") return "Create your account";
    return "Welcome back";
  }, [mode]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verified = params.get("verified");
    const verifyError = params.get("error");

    if (verified === "1") {
      setStatus("Email verified successfully. You can now sign in.");
    }

    if (verifyError) {
      const message =
        verifyError === "token_expired"
          ? "Verification link expired. Please request a new one."
          : verifyError === "invalid_token"
            ? "Invalid verification link. Please request a new one."
            : "Email verification failed. Please request a new link.";
      setError(message);
    }

    if (verified || verifyError) {
      params.delete("verified");
      params.delete("error");
      const next = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ""}`;
      window.history.replaceState({}, "", next);
    }

    const tick = () => {
      const state = getResendState(Date.now());
      setCooldownRemaining(state.cooldownRemaining);
      setAttemptsRemaining(state.attemptsRemaining);
    };

    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, []);

  const handleChange = (field: keyof FormState) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const resetMessages = () => {
    setError(null);
    setStatus(null);
  };

  const resendVerification = async (email: string) => {
    const state = getResendState(Date.now());
    if (!state.canSend) {
      if (state.attemptsRemaining <= 0) {
        setError("Resend limit reached. Please wait 15 minutes and try again.");
      } else {
        setError(`Please wait ${state.cooldownRemaining}s before resending.`);
      }
      return;
    }

    setSendingVerification(true);
    setError(null);
    setStatus(null);

    try {
      const { error: verificationError } = await authClient.sendVerificationEmail({
        email,
        callbackURL: `${loginURL}?verified=1`,
      });

      if (verificationError) throw verificationError;
      registerResendAttempt(Date.now());
      setStatus("Verification email sent. Please check your inbox.");
    } catch (err: any) {
      const rawMessage = err?.message || "Could not send verification email.";
      setError(
        /rate\s*limit|too\s*many|429/i.test(rawMessage)
          ? "Too many verification requests. Please wait a few minutes before trying again."
          : rawMessage,
      );
    } finally {
      setSendingVerification(false);
    }
  };

  const handleEmailAuth = async (event: React.FormEvent) => {
    event.preventDefault();
    resetMessages();

    if (!form.email) {
      setError("Please enter an email address.");
      return;
    }

    setUnverifiedEmail(null);

    try {
      if (!form.password) {
        setError("Please enter a password.");
        return;
      }

      if (isSignUp) {
        const { error: signUpError } = await authClient.signUp.email({
          email: form.email,
          password: form.password,
          name: form.name || form.email.split("@")[0] || "RetroRail User",
          callbackURL: `${loginURL}?verified=1`,
        });
        if (signUpError) throw signUpError;
        setMode("sign-in");
        setUnverifiedEmail(form.email);
        setStatus(
          "Account created. Please verify your email before signing in.",
        );
        return;
      }

      const { error: signInError } = await authClient.signIn.email({
        email: form.email,
        password: form.password,
        callbackURL: `${appURL}${postAuthPath}`,
      });

      if (signInError) throw signInError;
      navigate(postAuthPath);
    } catch (err: any) {
      const rawMessage = err?.message || "Something went wrong. Try again.";
      const message =
        /rate\s*limit|too\s*many|429/i.test(rawMessage)
          ? "Too many requests. Please wait a few minutes before trying again."
          : rawMessage;
      if (/verify|verification|email.*verified/i.test(message) && form.email) {
        setUnverifiedEmail(form.email);
      }
      setError(message);
    }
  };

  const handleSocial = async (provider: "google" | "github") => {
    resetMessages();
    await authClient.signIn.social({
      provider,
      callbackURL: `${appURL}${postAuthPath}`,
      errorCallbackURL: loginURL,
      newUserCallbackURL: `${appURL}${postAuthPath}`,
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900 flex items-center justify-center px-6">
      <div className="w-full max-w-md rounded-3xl border border-gray-200 bg-white p-8 shadow-xl">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.3em] text-indigo-600">
            RetroRail Access
          </p>
          <h1 className="mt-3 text-3xl font-semibold text-gray-900">
            {heading}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Sign in with email/password or your preferred provider.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handleSocial("google")}
            className="rounded-xl border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
          >
            Google
          </button>
          <button
            type="button"
            onClick={() => handleSocial("github")}
            className="rounded-xl border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
          >
            GitHub
          </button>
        </div>

        <div className="my-6 flex items-center gap-3 text-xs text-gray-400">
          <div className="h-px flex-1 bg-gray-200" />
          or continue with email
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        <form onSubmit={handleEmailAuth} className="space-y-4">
          {isSignUp && (
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-gray-500">
                Name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={handleChange("name")}
                placeholder="Ada Lovelace"
                className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-gray-500">
              Email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              placeholder="you@company.com"
              autoComplete="email"
              className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-gray-500">
              Password
            </label>
            <input
              type="password"
              value={form.password}
              onChange={handleChange("password")}
              placeholder="Enter your password"
              autoComplete="current-password"
              className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          {status && (
            <div className="rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
              {status}
            </div>
          )}
          {unverifiedEmail && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800 space-y-2">
              <p>
                Your email is not verified yet. Verify your email to continue.
              </p>
              <button
                type="button"
                onClick={() => resendVerification(unverifiedEmail)}
                disabled={sendingVerification || cooldownRemaining > 0 || attemptsRemaining <= 0}
                className="text-amber-900 font-medium underline disabled:opacity-60"
              >
                {sendingVerification
                  ? "Sending..."
                  : attemptsRemaining <= 0
                    ? "Resend limit reached"
                    : cooldownRemaining > 0
                      ? `Resend in ${cooldownRemaining}s`
                      : "Resend verification email"}
              </button>
              <p className="text-xs text-amber-700">
                Up to {RESEND_MAX_ATTEMPTS} resends every 15 minutes.
              </p>
            </div>
          )}

          <button
            type="submit"
            className="w-full rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            {isSignUp ? "Create account" : "Sign in"}
          </button>
        </form>

        <div className="mt-6 flex flex-wrap gap-3 text-xs text-gray-500">
          <button
            type="button"
            onClick={() => {
              setMode("sign-in");
              resetMessages();
            }}
            className={mode === "sign-in" ? "text-indigo-600" : ""}
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={() => {
              setMode("sign-up");
              resetMessages();
            }}
            className={mode === "sign-up" ? "text-indigo-600" : ""}
          >
            Sign up
          </button>
        </div>
      </div>
    </div>
  );
}
